import React from 'react';
import { Home, ChevronRight, Volume2, Users, ArrowRight, ArrowLeft, Mail } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface TommyDentonSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const TommyDentonSection: React.FC<TommyDentonSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb: The Dentons > Part 7 of 9: Tommy Denton */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 7 of 9: Tommy Denton
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Tommy Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          TOMMY DENTON (1886–1976)
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 7 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* World War One - the Egyptian Carpet and Gallipoli */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            World War One - the Egyptian Carpet and Gallipoli
          </h2>

          <p>
            When Tommy and Annie Denton were married in March 1919 one of the very special gifts they
            received was a small carpet¹ from Egypt. It was familiar to all their grandchildren and visitors as it
            was hung in the lounge at Oakdale.
          </p>

          {/* Image 97: Carpet */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/97 Carpet.png"
              alt="Carpet"
              caption="The Egyptian carpet gifted to Tommy and Annie Denton"
              filenameHint="97 Carpet.png"
            />
          </div>

          <p>
            It was the gift of Hilda Margaret Overton, the widow of Mayor Percy John Overton, who had worked
            with Tommy in Africa and Turkey in World War One, and who had known him in North Canterbury.
            Major Overton was killed in action on Aghyl Bair below Chunuk Bair in the north Anzac Section at
            Gallipoli, on 7 August 1915, aged 38. He was buried in Warley Gap. In 1919 after the war his body
            was exhumed and he was buried in the 7th Field Ambulance Cemetery, Gallipoli (part-pictured
            below), named after the Australian 7th Field Ambulance that landed there in September 1915.
          </p>

          <p>
            Percy's brother, Guy Stanley Overton, was also killed at Gallipoli - their parents got the sad news of
            each death within a few hours of each other. Hilda Overton, who was left with four children - Percy
            Leigh (who was killed in the Homer Tunnel, aged 32, on 7 July 1936), Reginald Charles (Bill), Elizabeth
            (Betty) Hilda, and Janice Margaret.
          </p>

          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#4a3525] pt-4">
            What was the link?
          </h3>

          <p>
            Percy Overton was a sheep farmer from Greta Valley, and a member of the Canterbury Mounted
            Rifles, with Tommy Denton who was living on the family farm at Domett.
          </p>

          {/* Image 98: Mounted Rifles */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/98 Mounted Riftles over the Port Hills to Lyttelton.jpg"
              alt="Mounted Riftles over the Port Hills to Lyttelton"
              caption="Tommy and his brother Harry are in this picture, with their horses. All the Mounted Rifles provided their own horses, looked after them on the ships, and then were appalled at the conditions facing them at Gallipoli. Many shot their horses rather than have them be badly treated or starved in the unsuitable conditions. Only four horses of the many thousands that were shipped away came back to NZ."
              filenameHint="98 Mounted Riftles over the Port Hills to Lyttelton.jpg"
            />
          </div>

          <p>
            Percy was in charge of scouting patrols at Gallipoli, and in a letter later published in The Press he
            wrote: "the first time I took Corporal Denton, and we had a great day together, and gained a lot of
            valuable information."
          </p>

          <p>
            They went over Rhododendron Ridge looking for a way up Chunuk Bair. Both men would have been
            used to hills and rocky outcrops, of treading silently through long grasses, of having the stillness that
            shepherds need. They had trained together in North Canterbury and sailed with the New Zealand
            Expeditionary Force through the Suez Canal to Alexandria, then by train to Cairo where they pitched
            tent camps within sight of the pyramids - an exotic, noisy, smelly and cosmopolitan contrast to the
            peaceful Greta Valley.
          </p>

          {/* Footnote 1 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p>1 The carpet hangs at Medbury Cottage, Kakanui (owned by Christine Negus)</p>
            <p>Cairo scene by German artist August Mache (1887-1914) Photo Courtesy Annette Benfield</p>
          </div>

          {/* Image 99: Major Percy Overton */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/99 Major Percy Overton.jpg"
              alt="Major Percy Overton"
              caption="Major Percy Overton (1877-1915), from Greta Valley to Gallipoli. He had earlier served in the Boer War in southern Africa. He was second in command of the Canterbury Mounted Rifles, beneath Major Acton-Adams. Much has been written of his leadership and character. He was a country boy, educated at Christ's College in Christchurch, a sheep farmer and a soldier."
              filenameHint="99 Major Percy Overton.jpg"
            />
          </div>

          <p>
            Tommy's Mounted Rifles uniform was donated to Canterbury Museum. His medals are in the
            protection of his granddaughter Annette Benfield.
          </p>

          {/* Images 100 and 101 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/100 dog tag.jpg"
              alt="dog tag"
              caption="Tommy Denton's dog tag"
              filenameHint="100 dog tag.jpg"
            />
            <ArchivalImageCard
              src="/images/101 service medals.jpg"
              alt="service medals"
              caption="Tommy Denton's service medals"
              filenameHint="101 service medals.jpg"
            />
          </div>
        </section>

        {/* NO GLAMOUR SHOULD BE ASSOCIATED WITH GALLIPOLI */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            NO GLAMOUR SHOULD BE ASSOCIATED WITH GALLIPOLI
          </h2>

          <p>
            Tommy was hospitalised five times from mid-May 1915, when he arrived at Gallipoli, and August. He
            was sent to Cairo to convalesce until he arrived back in New Zealand in March 1916. He was carried
            off the hospital ship on a stretcher, exactly half the weight he was when he left - from 76 kg to 38 kg.
          </p>

          {/* Image 102: Hospital ship */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/102 Hospital ship.jpg"
              alt="Hospital ship"
              caption="Hospital ship"
              filenameHint="102 Hospital ship.jpg"
            />
          </div>

          <p>
            Taking wounded and sick men to the hospital ships to take them to Egypt, Malta or England, and
            using stretchers to take them off the ship to go to hospitals in Egypt, Malta and England.
          </p>

          {/* Image 103: Loading men onto hospital ship */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/103 Loading men onto hospital ship.jpg"
              alt="Loading men onto hospital ship"
              caption="Loading men onto hospital ship"
              filenameHint="103 Loading men onto hospital ship.jpg"
            />
          </div>

          <p className="font-bold text-[#4a3525]">
            The Imperial War Museum lists nine reasons why Gallipoli was one of the worst fronts in WW1.
          </p>

          <ol className="list-decimal pl-6 space-y-3 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] font-serif text-sm sm:text-base text-[#2c221e]">
            <li>Fly swarms</li>
            <li>
              Unappetising food, mostly stringy and salty poor quality bully beef in tins from
              Uruguay, biscuits so hard they could break the men's teeth, inferior jam, sugar, very
              little water and beef cubes. It was so lacking in any nutritional value that most of the
              men soon had at least the early stages of scurvy.
            </li>
            <li>
              Very little water, which had to be transported up from the ships as there was no
              water on the peninsula, often less than half a cup per man a day
            </li>
            <li>Extreme weather from heat in summer to rain, snow, and bone-chilling winds in winter</li>
            <li>Lice infestations</li>
          </ol>

          {/* Image 104: Gallipoli conditions */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/104 Gallipoli conditions.jpg"
              alt="Gallipoli conditions"
              caption="Gallipoli conditions"
              filenameHint="104 Gallipoli conditions.jpg"
            />
          </div>

          <ol start={6} className="list-decimal pl-6 space-y-3 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] font-serif text-sm sm:text-base text-[#2c221e]">
            <li>Rotting corpses in no man's land</li>
            <li>Dysentery epidemic, with 85% of the Kiwis suffering from this</li>
            <li>Basic latrines</li>
          </ol>

          {/* Image 105: Gallipoli camp */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/105 Gallipoli camp.jpg"
              alt="Gallipoli camp"
              caption="Gallipoli Camp"
              filenameHint="105 Gallipoli camp.jpg"
            />
          </div>

          <ol start={9} className="list-decimal pl-6 space-y-3 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] font-serif text-sm sm:text-base text-[#2c221e]">
            <li>Inhospitable terrain</li>
          </ol>

          {/* Image 106: The Landing at Gallipoli */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/106 The Landing at Gallipoli.png"
              alt="The Landing at Gallipoli"
              caption="The landing, 25 April 1915"
              filenameHint="106 The Landing at Gallipoli.png"
            />
          </div>

          {/* Image 107: trenches */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/107 trenches.jpg"
              alt="trenches"
              caption="The trenches, which today to walk through seem benign but were crawling with maggots"
              filenameHint="107 trenches.jpg"
            />
          </div>

          <p>
            And perhaps one of the worst images of all, going over the top in the near certain
            knowledge of what was ahead.
          </p>

          {/* Image 108: Over the Top */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/108 Over the Top.jpg"
              alt="Over the Top"
              caption="Over the Top"
              filenameHint="108 Over the Top.jpg"
            />
          </div>

          {/* Image 109: Tom and Annie Denton, 1968 */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/109 Tom and Annie Denton, 1968.jpg"
              alt="Tom and Annie Denton, 1968"
              caption="Tom and Annie Denton, 1968"
              filenameHint="109 Tom and Annie Denton, 1968.jpg"
            />
          </div>

          <p>
            Tommy survived and lived a long and fulfilling life with Annie. This was taken in 1968 at a debutante
            dinner for their granddaughter Christine Negus.
          </p>

          <p>
            Several family members have been to Gallipoli. For each it has been an emotional
            experience. For some the futility of it, for others the stupidity of the ruling classes, for all the
            deep sadness for all those young lives lost for no good reason.
          </p>

          <p>
            The annual remembrance services have become tourist attractions in their own right, but no
            one must let the horror be sanitised in any way whatsoever. Making it an event does not
            make it unforgiven.
          </p>

          <p>
            As one gets older, the waste of life seems a greater crime - perhaps as we now understand
            how short our time really is.
          </p>

          <p>
            BUT one last thing..... Tommy got a love of fresh figs in Turkey and he loved figs for the rest
            of his life. A real treat was to take him lovely fresh figs, which he ate very, very, slowly.
          </p>
        </section>

        {/* THE SHIFT FROM MOTUNAU TO SOUTHBRIDGE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            THE SHIFT FROM MOTUNAU TO SOUTHBRIDGE
          </h2>

          <h3 className="font-serif text-xl font-bold text-[#4a3525]">
            Tommy rode to and from Ashburton to see his eldest sister Maggie
          </h3>

          <p>
            Toomy got his first property when he was 24, being successful in a ballot, after eight years of
            working on home and other farms. It was close to Motunau and he was a keen fisherman who
            caught and sold fresh fish as a sideline. It was only about 65 ha so it was a big step to sell to John
            Byrch in 1927 and shift south to an undeveloped 400 ha on the north side of the Rakaia River. The
            dwelling was more a collection of huts but there was space and opportunity, tussock flats where
            cattle and sheep could spread out. Paddocks were fenced, plantations were planted, ground was
            cleared and ploughed, thousands of rabbits were caught.
          </p>

          <p>
            Tommy would live there for the next 49 years. His son Warren gradually took over the management
            and after Warren's marriage in 1956 Tom and Annie moved into a new house south of the original
            home where Warren built a new home for his family. There he established another large orchard
            and big vegetable garden.
          </p>

          <p>
            When Tommy moved to Feredays Road he was a lot closer to his eldest sister, Margaret Esther who
            was married to Ernest Keig. They lived in Ashburton where Ernest was a dairy factory manager. They
            had four children - Vera, Madge, John and Lil.
          </p>

          <p>
            Tommy often went by horseback to spend the day with them and then riding home. His youngest
            daughter Heather recalled that he went through the top of Oakdale, across the river flats, and found
            his way across the wide Rakaia River, thereby cutting out the long straight ride to the Rakaia Bridge.
            It was a return ride of about 65 km.
          </p>

          {/* Image 110: Rakaia Railway Bridge under construction */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/110 Rakaia Railway Bridge under construction.jpg"
              alt="Rakaia Railway Bridge under construction"
              caption="The construction of the railway bridge over the Rakaia River, 1938-39"
              filenameHint="110 Rakaia Railway Bridge under construction.jpg"
            />
          </div>
        </section>

        {/* LETTERS FROM WORLD WAR ONE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            LETTERS FROM WORLD WAR ONE
          </h2>

          <h3 className="font-serif text-xl font-bold text-[#4a3525]">
            MORRIS GIBB letters to his sisters, Helen and Annie
          </h3>

          {/* Morris Letter 1 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="flex items-center gap-2 text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider">
              <Mail className="w-4 h-4" />
              <span>No 2 N.Z. Glen. Hosp. Walton-on-Thames — 6/9/18</span>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Nell</p>
              <p>
                I have delayed writing for the past week owing to the fact that I have been travelling most of the
                time. Also, I have been feeling very sorry for myself, very much so. It was the 31st August that the
                mishap occurred early in the morning don't know the exact time, I did not wait to see. I was satisfied
                I had seen any amount to satisfy me for the time. You would probably get word of this lot while the
                letters I wrote in dock last time are just coming through.
              </p>
              <p>
                Well to tell you the truth I had only been in the Coy three days, joined them late Tuesday and was
                whanged on the Saturday, so I was with the boys for a very shorty period also with another
                battalion, 1st W.T.B. instead of second. After several days of being messed about in trains,
                ambulances etc, I arrived here a couple of days ago and am getting settled at last.
              </p>
              <p>
                As far as the wound is concerned, they say it is doing splendid. If it is anything like what it feels, it is
                pretty right. Really the wound troubles me less than being bed sore, though the stiffness may wear
                off in a few days. It was a bullet that got me in the right thigh and went right through, so I will have a
                long spell this time.
              </p>
              <p>
                Most unlikely that I will see France again this year and perhaps it may be seven or eight months
                before I am hostile again. Although it is a pretty severe crack don't get building up hopes of me
                coming back, for that is most improbable, though I will not say impossible for anything may turn up.
              </p>
              <p>
                Well, this is pretty awkward so will close hoping that everything is going well. I suppose Dad would
                be a bit upset when he got a cable.
              </p>
              <p className="not-italic pt-2">
                I remain<br />
                Your loving brother<br />
                Morris
              </p>
            </div>
          </div>

          {/* Morris Letter 2 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="flex items-center gap-2 text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider">
              <Mail className="w-4 h-4" />
              <span>8/11/17</span>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Annie</p>
              <p>
                This is the first time I have started to write to you since coming over to France. If a chap in the tent
                had not managed to get measles, I would most likely have been up the line by now.
              </p>
              <p>
                As it is I will be here for three weeks at the least, perhaps longer. We see a fair few French people
                and what I have seen are a very poor class of people and also pretty dirty. Remember the Hubert's?
                Fred and I have been together every night lately and I can tell you I was pleased to see him, it was a
                pleasant surprise one morning when I came face to face with him. We had our photo's taken
                together last Sunday and Fred will get them in a few days.
              </p>
              <p>
                I am properly isolated this time we had to pull up our tent and shift right away from the rest of the
                camp so I may not see Fred before going up the line.
              </p>
              <p>
                I was very sorry to hear that Burnie was killed but accidents happen over here sometimes, pretty
                often in fact. Fred saw Dave and from what I can gather Dave and his party were being relieved by
                the lot Burnie was in when he (suppose others were with him) was blown up by a shell. I was pleased
                someone who knew him was there and I hope Dave has written to them at Glen-Clova as he knows
                more about it more than anyone.
              </p>
              <p>
                I hope Dad is right again now as it would be very awkward for him with a poisoned hand this time of
                the year.
              </p>
              <p>
                Well Annie, shearing will be over and the harvest well in by the time you get this. This is all I can say
                just now.
              </p>
              <p className="not-italic pt-2">
                I remain<br />
                Your loving brother<br />
                Morris
              </p>
            </div>
          </div>

          {/* Morris Letter 3 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="flex items-center gap-2 text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider">
              <Mail className="w-4 h-4" />
              <span>N.Z. Base 13/8/18</span>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Annie</p>
              <p>
                You will see by above that I am at the base again. It is over eight months since I was here last, and it
                was late in the season then.
              </p>
              <p>
                At present it is a very different place, and the weather is perfect. Harvesting is in full swing here now
                and the way they do things seems very old fashioned after our own methods of handling the same
                things.
              </p>
              <p>
                Fred Wilkie was here but he went forward with the last draft going up. He is just the same as the rest
                of his crowd and you know what they are like. Myself, I am on the next draft but when they go
                forward, I am not sure. One thing is pretty certain and that is it won't be very long.
              </p>
              <p>
                Ray Stanton wrote to me a while back he had got my address from Fred. At the time Ray was with
                the entrenching Battalion but expected to join his unit very shortly. That was some time ago, so he
                most probably is well into it by this time. Fred seems to be doing pretty well he was behind the line
                in reserve the last letter I had from him, but that took a month to reach me so I can't say where he is
                now.
              </p>
              <p>
                I have a good bath every day here and it goes well. The Bath House is the best I have been in
                anywhere, fitted with hot and cold showers and a good big plunge bath about four and a half feet
                deep. That is the one thing I will miss when I move up. Any way I done it before so I suppose I can do
                the same again.
              </p>
              <p>
                There has been a mail in but mine has not come through yet, I suppose it is chasing me all round the
                different camps I have been in. Though it is bound to get me in time. By the way time cures
                everything here. It is over nine weeks since I left the line, so I have done pretty well out of the
                business this time.
              </p>
              <p>
                I have a considerable amount of writing to do if possible before I leave here so I will close hoping this
                finds all well.
              </p>
              <p className="not-italic pt-2">
                I remain<br />
                your loving brother<br />
                Morris
              </p>
            </div>
          </div>

          <h3 className="font-serif text-xl font-bold text-[#4a3525] pt-4">
            TOMMY'S LETTER TO ANNIE
          </h3>

          {/* Tommy Letter */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="flex items-center gap-2 text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider">
              <Mail className="w-4 h-4" />
              <span>France — 26/8/18</span>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p className="font-bold text-xs uppercase text-[#7a5b3a]">Page 1</p>
              <p>My dear old Annie</p>
              <p>
                Just two lines to let you know that everything is OK with the old man. Am still tramping down the
                daisies not pushing them up. I had a letter from you a few days ago and am pleased to hear you are
                getting on well.
              </p>
              <p>
                When is the important event coming off? I must get a piece of cake when it does. We are having a
                quiet time of it now, nothing much doing in Picardy. Had a note from Morris the other day, he is in
                hospital with a girl in as well. I don't know where the girl is
              </p>
              <p className="font-bold text-xs uppercase text-[#7a5b3a] pt-2">Page 4</p>
              <p>
                ...and Lina. I had a note from Lina, very kind of her to write. She says she is still going to school. She
                must be a big girl now. Have you seen the Morrison's since they left? They will find it lonely up at the
                Greta. I must close these few lines now as the mail closes in 20 minutes, so I will draw the
              </p>
              <p className="not-italic pt-2">
                From yours sincerely<br />
                Thos. Denton
              </p>
            </div>
          </div>
        </section>

        {/* Navigation Footer to Previous/Next Chapter */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => onSelectPart && onSelectPart('part6')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Part 6: Isaac Ephraim Denton</span>
          </button>

          <button
            onClick={() => onSelectPart && onSelectPart('part8')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <span>Part 8: Harry Denton WW2</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </article>
    </div>
  );
};
